import React, { useState, useEffect } from "react";
import {
  PublicClientApplication,
  InteractionStatus,
  AccountInfo,
} from "@azure/msal-browser";
import {
  MsalProvider,
  useMsal,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
} from "@azure/msal-react";
import {
  EuiPage,
  EuiPageBody,
  EuiEmptyPrompt,
  EuiIcon,
  EuiButton,
  EuiCallOut,
  EuiPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiLoadingSpinner,
  EuiPageContent,
} from "@elastic/eui";
import { User } from "./types";
import { useAnketaI18n } from "src/internationalization";

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID!,
    authority: `https://login.microsoftonline.com/common`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "sessionStorage" as const,
    storeAuthStateInCookie: false,
  },
};

// Erweiterte Scopes für Microsoft Graph API
const loginRequest = {
  scopes: [
    "User.Read",
    "User.ReadBasic.All",
    "Directory.Read.All",
    "Group.Read.All",
    "openid",
    "profile",
    "email",
  ],
};

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

interface MicrosoftAuthProps {
  onAuthSuccess: (user: User) => void;
}

interface GraphUserData {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
}

interface GraphGroup {
  id: string;
  displayName: string;
  description?: string;
}

const MicrosoftLoginContent: React.FC<MicrosoftAuthProps> = ({
  onAuthSuccess,
}) => {
  const { translate } = useAnketaI18n();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [error, setError] = useState<string | null>(null);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      fetchUserDataFromMicrosoft(accounts[0]);
    }
  }, [isAuthenticated, accounts]);

  const fetchUserDataFromMicrosoft = async (account: AccountInfo) => {
    setIsLoadingUserData(true);
    try {
      // Hole Access Token für Microsoft Graph
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account: account,
      });

      const accessToken = tokenResponse.accessToken;

      // Hole User-Daten von Microsoft Graph
      const [userData, userGroups, userLicenses] = await Promise.all([
        fetchGraphUser(accessToken),
        fetchGraphGroups(accessToken),
        fetchGraphLicenses(accessToken),
      ]);

      // Sende Daten an dein Backend zur Verarbeitung
      const userRole = await sendToBackend({
        microsoftId: userData.id,
        email: userData.mail || userData.userPrincipalName,
        name: userData.displayName,
        department: userData.department,
        location: userData.officeLocation,
        groups: userGroups,
        licenses: userLicenses,
        accessToken: accessToken,
      });

      // Erstelle User-Objekt für Frontend
      const user: User = {
        id: userData.id,
        name: userData.displayName,
        email: userData.mail || userData.userPrincipalName,
        role: userRole,
      };

      onAuthSuccess(user);
    } catch (err: any) {
      console.error("Error fetching user data:", err);
      setError(
        translate("anketa.medocino.support.auth.error.loadUserData", {
          defaultMessage:
            "Failed to load user data. Please try again.",
        })
      );
    } finally {
      setIsLoadingUserData(false);
    }
  };

  // Fetch User from Microsoft Graph
  const fetchGraphUser = async (
    accessToken: string
  ): Promise<GraphUserData> => {
    const response = await fetch("https://graph.microsoft.com/v1.0/me", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch user data from Microsoft Graph");
    }

    return response.json();
  };

  // Fetch User Groups from Microsoft Graph
  const fetchGraphGroups = async (
    accessToken: string
  ): Promise<GraphGroup[]> => {
    const response = await fetch(
      "https://graph.microsoft.com/v1.0/me/memberOf",
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch user groups from Microsoft Graph");
    }

    const data = await response.json();
    return data.value || [];
  };

  // Fetch User Licenses from Microsoft Graph
  const fetchGraphLicenses = async (accessToken: string): Promise<string[]> => {
    const response = await fetch(
      "https://graph.microsoft.com/v1.0/me/licenseDetails",
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch user licenses from Microsoft Graph");
    }

    const data = await response.json();
    return data.value?.map((license: any) => license.skuPartNumber) || [];
  };

  // Send data to your backend for processing
  const sendToBackend = async (data: {
    microsoftId: string;
    email: string;
    name: string;
    department?: string;
    location?: string;
    groups: GraphGroup[];
    licenses: string[];
    accessToken: string;
  }): Promise<"agent" | "reviewer" | "admin"> => {
    // No catch/fallback here: a failed backend authorization must NEVER silently
    // grant a privileged role. Errors propagate to the caller (handleAuth), which
    // shows a visible error and does not call onAuthSuccess, so the user is denied.
    const response = await fetch("/api/auth/microsoft/callback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Backend authentication failed (${response.status})`);
    }

    const result = await response.json();
    const role = result.role;
    if (role !== "agent" && role !== "reviewer" && role !== "admin") {
      // Don't default to a privileged role when the backend omits/returns junk.
      throw new Error("Backend did not return a valid role");
    }
    return role;
  };

  const handleLogin = async () => {
    try {
      setError(null);
      await instance.loginPopup(loginRequest);
    } catch (err: any) {
      console.error("Login error:", err);
      setError(
        err.message ||
          translate("anketa.medocino.support.auth.error.loginFailed", {
            defaultMessage: "Login failed. Please try again.",
          })
      );
    }
  };

  const isLoading = inProgress !== InteractionStatus.None || isLoadingUserData;

  if (isLoading) {
    return (
      <EuiPage paddingSize="none">
        <EuiPageBody>
          <EuiPageContent>
            <EuiEmptyPrompt
              icon={<EuiLoadingSpinner size="xl" />}
              title={
                <h2>
                  {isLoadingUserData
                    ? translate(
                        "anketa.medocino.support.auth.loading.userData",
                        { defaultMessage: "Loading user data..." }
                      )
                    : translate(
                        "anketa.medocino.support.auth.loading.signingIn",
                        { defaultMessage: "Signing in..." }
                      )}
                </h2>
              }
              body={
                <p>
                  {isLoadingUserData
                    ? translate(
                        "anketa.medocino.support.auth.loading.fetchingInfo",
                        { defaultMessage: "Fetching information from Microsoft..." }
                      )
                    : translate(
                        "anketa.medocino.support.auth.loading.redirecting",
                        {
                          defaultMessage:
                            "You are being redirected to Microsoft. Please wait a moment.",
                        }
                      )}
                </p>
              }
            />
          </EuiPageContent>
        </EuiPageBody>
      </EuiPage>
    );
  }

  return (
    <EuiPage paddingSize="none">
      <EuiPageBody>
        <EuiPageContent>
          <UnauthenticatedTemplate>
            <EuiPanel style={{ maxWidth: "600px", margin: "0 auto" }}>
              <EuiFlexGroup
                direction="column"
                alignItems="center"
                gutterSize="l"
              >
                <EuiFlexItem>
                  <EuiIcon type="logoElastic" size="xxl" />
                </EuiFlexItem>

                <EuiFlexItem>
                  <EuiText textAlign="center">
                    <h1>
                      {translate("anketa.medocino.support.auth.title", {
                        defaultMessage: "Enterprise Onboarding System",
                      })}
                    </h1>
                  </EuiText>
                </EuiFlexItem>

                <EuiFlexItem>
                  <EuiText textAlign="center" color="subdued">
                    <p>
                      {translate("anketa.medocino.support.auth.welcome", {
                        defaultMessage:
                          "Welcome! Sign in with your Microsoft account to continue.",
                      })}
                    </p>
                  </EuiText>
                </EuiFlexItem>

                {error && (
                  <EuiFlexItem style={{ width: "100%" }}>
                    <EuiCallOut
                      title={translate(
                        "anketa.medocino.support.auth.error.loginFailed.title",
                        { defaultMessage: "Login failed" }
                      )}
                      color="danger"
                      iconType="alert"
                    >
                      <p>{error}</p>
                    </EuiCallOut>
                  </EuiFlexItem>
                )}

                <EuiFlexItem>
                  <EuiButton
                    fill
                    size="m"
                    onClick={handleLogin}
                    iconType="logoAzureMono"
                    iconSide="left"
                    style={{ minWidth: "250px" }}
                  >
                    {translate("anketa.medocino.support.auth.signInButton", {
                      defaultMessage: "Sign in with Microsoft",
                    })}
                  </EuiButton>
                </EuiFlexItem>

                <EuiFlexItem>
                  <EuiText size="xs" color="subdued" textAlign="center">
                    <p>
                      {translate(
                        "anketa.medocino.support.auth.consent.prefix",
                        { defaultMessage: "By signing in, you agree to our" }
                      )}{" "}
                      <a href="#terms">
                        {translate(
                          "anketa.medocino.support.auth.consent.terms",
                          { defaultMessage: "terms of use" }
                        )}
                      </a>{" "}
                      {translate("anketa.medocino.support.auth.consent.and", {
                        defaultMessage: "and",
                      })}{" "}
                      <a href="#privacy">
                        {translate(
                          "anketa.medocino.support.auth.consent.privacy",
                          { defaultMessage: "privacy policy" }
                        )}
                      </a>
                      .
                    </p>
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiPanel>
          </UnauthenticatedTemplate>

          <AuthenticatedTemplate>
            <EuiEmptyPrompt
              icon={<EuiLoadingSpinner size="xl" />}
              title={
                <h2>
                  {translate(
                    "anketa.medocino.support.auth.authSuccess.title",
                    { defaultMessage: "Authentication successful" }
                  )}
                </h2>
              }
              body={
                <p>
                  {translate(
                    "anketa.medocino.support.auth.authSuccess.body",
                    { defaultMessage: "Loading user data from Microsoft..." }
                  )}
                </p>
              }
            />
          </AuthenticatedTemplate>
        </EuiPageContent>
      </EuiPageBody>
    </EuiPage>
  );
};

export const MicrosoftAuth: React.FC<MicrosoftAuthProps> = ({
  onAuthSuccess,
}) => {
  return (
    <MsalProvider instance={msalInstance}>
      <MicrosoftLoginContent onAuthSuccess={onAuthSuccess} />
    </MsalProvider>
  );
};

export { msalInstance };
